import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';
import { API_BASE_URL } from '../../config';
import { ProductOption, parseSplitVolume, productChoices } from '../../utils/batchProducts';

interface SplitPlan {
  batchId: string;
  productId: number | string;
  productName: string;
  siteId: string;
  left: number | null;
  leftBasis: 'vessel' | 'remaining';
  childBatchId: string;
  equipmentId: number | null;
}

interface Props {
  batchId: string;
  onCancel: () => void;
  onDone: (message: string, result: { childBatchId: string }) => void;
}

const fmt = (n: number | null | undefined) => (n == null ? '—' : String(Math.round(Number(n) * 100) / 100));

/**
 * "Split batch": move X bbl into a new child batch <id>.<n> with a chosen product
 * and optional tank. POST /api/batches/:id/split (batch rows + events only).
 */
const SplitBatchModal: React.FC<Props> = ({ batchId, onCancel, onDone }) => {
  const [plan, setPlan] = useState<SplitPlan | null>(null);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [tanks, setTanks] = useState<{ equipmentId: number; name: string; abbreviation?: string | null; type?: string | null }[]>([]);
  const [volume, setVolume] = useState('');
  const [productId, setProductId] = useState('');
  const [equipmentId, setEquipmentId] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/batches/${encodeURIComponent(batchId)}/split`, { headers: { Accept: 'application/json' } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        if (cancelled) return;
        setPlan(data);
        const [p, e] = await Promise.all([
          apiFetch(`${API_BASE_URL}/api/products`, { headers: { Accept: 'application/json' } }).then((r) => r.json()).catch(() => []),
          apiFetch(`${API_BASE_URL}/api/equipment?siteId=${encodeURIComponent(data.siteId)}&available=1`, { headers: { Accept: 'application/json' } })
            .then((r) => r.json())
            .catch(() => []),
        ]);
        if (cancelled) return;
        setProducts(Array.isArray(p) ? p : []);
        setTanks(Array.isArray(e) ? e : []);
      } catch (err: any) {
        if (!cancelled) setError('Could not load the batch: ' + err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId]);

  const choices = plan ? productChoices(products, plan) : [];
  const parsed = plan ? parseSplitVolume(volume, plan.left) : {};
  const leftAfter = plan && parsed.value != null && plan.left != null ? Math.max(0, plan.left - parsed.value) : null;

  const confirm = async () => {
    if (!plan) return;
    const v = parseSplitVolume(volume, plan.left);
    if (v.error) return setError(v.error);
    if (!productId) return setError('Choose the product for the new batch.');
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/batches/${encodeURIComponent(batchId)}/split`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ volume: v.value, productId, ...(equipmentId ? { equipmentId: Number(equipmentId) } : {}), reason: reason.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      onDone(data.message || `Split into ${data.childBatchId}`, { childBatchId: data.childBatchId });
    } catch (err: any) {
      setError('Split failed: ' + err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="facility-modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="split-title" data-testid="split-modal">
      <div className="facility-modal">
        <h3 id="split-title">Split {batchId}</h3>
        {plan && (
          <p className="facility-modal__meta" data-testid="split-reference">
            <strong>{plan.productName}</strong>
            <br />
            {fmt(plan.left)} bbl left{plan.leftBasis === 'vessel' ? ' in its tank' : ''} · new batch <strong>{plan.childBatchId}</strong>
            {leftAfter != null && (
              <>
                <br />
                After the split: {fmt(parsed.value)} bbl in {plan.childBatchId}, {fmt(leftAfter)} bbl left in {batchId}
              </>
            )}
          </p>
        )}
        <label htmlFor="split-volume">Volume to split off (bbl)</label>
        <input
          id="split-volume"
          data-testid="split-volume"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={volume}
          onChange={(e) => setVolume(e.target.value)}
          placeholder="e.g. 7"
          autoFocus
        />
        <label htmlFor="split-product">Product for the new batch</label>
        <select id="split-product" data-testid="split-product" value={productId} onChange={(e) => setProductId(e.target.value)}>
          <option value="">Choose a product…</option>
          {choices.map((p) => (
            <option key={String(p.id)} value={String(p.id)}>
              {p.name}
              {plan && String(p.id) === String(plan.productId) ? ' (same as now)' : ''}
            </option>
          ))}
        </select>
        <label htmlFor="split-tank">Tank (optional)</label>
        <select id="split-tank" data-testid="split-tank" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
          <option value="">No tank yet</option>
          {tanks.map((t) => (
            <option key={t.equipmentId} value={t.equipmentId}>
              {t.abbreviation || t.name}
              {t.type ? ` (${t.type})` : ''}
            </option>
          ))}
        </select>
        <label htmlFor="split-reason">Note (optional)</label>
        <input id="split-reason" data-testid="split-reason" type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Half to strawberry" />
        <p className="facility-modal__hint">Packaging is counted on whichever batch you package from. No inventory or TTB records change.</p>
        {error && (
          <div className="facility-modal__error" role="alert" data-testid="split-error">
            {error}
          </div>
        )}
        <div className="facility-modal__actions">
          <button type="button" className="is-primary" data-testid="split-confirm" onClick={confirm} disabled={busy || !plan}>
            {busy ? 'Saving…' : 'Split batch'}
          </button>
          <button type="button" className="is-secondary" data-testid="split-cancel" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default SplitBatchModal;
